"use client";
import React from "react";
import type { CSSProperties } from "react";
import { Button, Divider, Flex, List, Modal } from "antd";
import { resolveCatalogRequirementLabel } from "@openings/booking-core";
import { DefaultService, SelectedService } from "./types";
import {
  TEMPLATE_LINE_HEIGHT_BODY,
  TEMPLATE_TYPE_SCALE,
} from "../../../TemplateSectionStyle";

interface CombineServicesProps {
  services: DefaultService[];
  selectedServices: SelectedService[];
  showPrice?: boolean;
  /** See `SelectService`'s `exactTerms`. */
  exactTerms?: boolean;
  onSubmit: (services: SelectedService[]) => void;
  onClose: () => void;
}

const MultipleServicesModal = (props: CombineServicesProps) => {
  const headingStyle: CSSProperties = {
    fontWeight: 600,
    fontSize: TEMPLATE_TYPE_SCALE.body,
    lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
  };
  const bodyStyle: CSSProperties = {
    fontSize: TEMPLATE_TYPE_SCALE.body,
    lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
  };
  const smallStyle: CSSProperties = {
    fontSize: TEMPLATE_TYPE_SCALE.small,
    lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
    opacity: 0.7,
  };
  const [selectedServices, setSelectedServices] = React.useState(
    props.selectedServices,
  );

  const onAddService = (service: DefaultService) => {
    setSelectedServices((prev) => [...prev, service]);
  };

  const onRemoveService = (service: DefaultService) => {
    setSelectedServices((prev) => {
      const indexToRemove = prev.findIndex(
        (selectedService) => selectedService.id === service.id,
      );
      if (indexToRemove === -1) return prev;
      const updatedServices = [...prev];
      updatedServices.splice(indexToRemove, 1);
      return updatedServices;
    });
  };

  let totalDuration = 0;
  let totalPrices = 0;

  selectedServices.forEach((service) => {
    totalDuration += service.duration;
    totalPrices += service.price ?? 0;
  });

  return (
    <Modal
      open={true}
      title="Build Services"
      onCancel={props.onClose}
      zIndex={10100}
      footer={null}
      // Portals to document.body; a booking skin re-scopes its vars to this
      // class (inert on the flat presets and outside a booking root).
      rootClassName="openings-booking-portal"
    >
      <div>
        <List
          itemLayout="horizontal"
          dataSource={props.services}
          renderItem={(item) => {
            let cnt = 0;
            selectedServices.forEach((service) => {
              if (service.id === item.id) cnt++;
            });
            // Stated where the service is added, before a time or contact
            // details (BF-DRIFT-005).
            const requirement = resolveCatalogRequirementLabel([item], {
              exactTerms: props.exactTerms ?? false,
            });
            return (
              <List.Item
                actions={[
                  <Button
                    key="1"
                    onClick={() => onAddService(item)}
                    size="small"
                  >
                    +
                  </Button>,
                  <Button
                    key="2"
                    onClick={() => onRemoveService(item)}
                    size="small"
                  >
                    -
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                      }}
                    >
                      <span style={bodyStyle}>{item.title}</span>
                      {props.showPrice &&
                      typeof item.price === "number" &&
                      item.price > 0 ? (
                        <>
                          <Divider orientation="vertical" />
                          <span style={smallStyle}>
                            ${item.price}
                            {item.up ? "+" : ""}
                          </span>
                        </>
                      ) : null}
                      {requirement ? (
                        <>
                          <Divider orientation="vertical" />
                          <span
                            data-booking-service-requirement
                            style={smallStyle}
                          >
                            {requirement}
                          </span>
                        </>
                      ) : null}
                    </div>
                  }
                  description={
                    item.description ? (
                      <span style={smallStyle}>{item.description}</span>
                    ) : null
                  }
                />
                <span style={headingStyle}>{cnt}</span>
              </List.Item>
            );
          }}
        />

        {props.showPrice && totalPrices > 0 && (
          <>
            <Divider />
            <Flex justify="space-between">
              <span style={headingStyle}>Total</span>
              <span style={bodyStyle}>${totalPrices}</span>
            </Flex>
          </>
        )}

        <Divider />
        <Flex justify="space-between" align="center">
          <span style={headingStyle}>Total:</span>
          <span style={bodyStyle}>
            {selectedServices.length} service
            {selectedServices.length > 1 ? "s" : ""}
          </span>
        </Flex>

        <Divider />
        <Button
          disabled={selectedServices.length === 0}
          onClick={() => props.onSubmit(selectedServices)}
          type="primary"
          block
        >
          Find Openings
        </Button>
      </div>
    </Modal>
  );
};

export default MultipleServicesModal;
